import { useState } from 'react'
import { Shield, Loader2, RefreshCw, ChevronDown, ChevronRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useBuildReviews, useBuildReviewCompliance } from '@/hooks/useApi'
import { timeAgo } from '@/lib/utils'

const VERDICT_CONFIG: Record<string, { color: string; icon: typeof CheckCircle; label: string }> = {
  safe: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, label: 'Safe' },
  safe_with_conditions: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertCircle, label: 'Conditions' },
  not_safe: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, label: 'Not Safe' },
}

export function BuildReviewPanel() {
  const [verdictFilter, setVerdictFilter] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: reviews, isLoading: reviewsLoading, error: reviewsError, refetch } = useBuildReviews(verdictFilter || undefined)
  const { data: compliance, isLoading: complianceLoading, error: complianceError } = useBuildReviewCompliance()

  const entries = reviews?.reviews ?? []

  if (reviewsError && complianceError && !reviewsLoading && !complianceLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-surface text-text-muted text-xs">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Build review data available after Q41 backend is fully deployed.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {compliance && !complianceLoading && !compliance.gate_active && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs">
          <AlertCircle className="h-4 w-4 shrink-0" />
          No build reviews recorded in the last 30 days. Run{' '}
          <code className="font-mono bg-amber-500/10 px-1 rounded">require_build_review</code>{' '}
          before deploying.
        </div>
      )}

      {compliance && !complianceLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Last 30 days', value: compliance.reviews_last_30_days, danger: false },
            { label: 'Last 7 days', value: compliance.reviews_last_7_days, danger: false },
            { label: 'Not Safe (7d)', value: compliance.not_safe_last_7_days, danger: compliance.not_safe_last_7_days > 0 },
            { label: 'With Conditions (7d)', value: compliance.safe_with_conditions_last_7_days, danger: false },
          ].map(({ label, value, danger }) => (
            <div key={label} className={`px-4 py-3 rounded-lg border ${danger ? 'bg-red-500/10 border-red-500/30' : 'bg-surface-elevated border-border'}`}>
              <div className={`text-xl font-mono font-bold ${danger ? 'text-red-400' : 'text-text-primary'}`}>{value}</div>
              <div className="text-[10px] text-text-muted mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Build Reviews
            {(reviewsLoading || complianceLoading) && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            <div className="ml-auto flex items-center gap-2">
              {reviews && <Badge variant="outline">{reviews.total} total</Badge>}
              <button onClick={() => refetch()} className="p-1.5 text-text-muted hover:text-text-primary transition-colors rounded">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4 flex-wrap items-center">
            {([['', 'All'], ['safe', 'Safe'], ['safe_with_conditions', 'Conditions'], ['not_safe', 'Not Safe']] as const).map(([v, label]) => (
              <button
                key={v}
                onClick={() => setVerdictFilter(v)}
                className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                  verdictFilter === v ? 'bg-accent/20 text-accent border-accent/30' : 'text-text-muted border-border hover:border-accent/30'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {entries.length === 0 && !reviewsLoading && (
            <div className="text-center py-8 text-text-muted text-sm">No build reviews found.</div>
          )}

          <div className="space-y-1">
            {entries.map((entry) => {
              const vc = VERDICT_CONFIG[entry.overall_verdict] ?? VERDICT_CONFIG.safe
              const VIcon = vc.icon
              const isExpanded = expandedId === entry.review_id
              return (
                <div key={entry.review_id}>
                  <div
                    onClick={() => setExpandedId(isExpanded ? null : entry.review_id)}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-border hover:border-accent/30 transition-colors cursor-pointer bg-surface-elevated"
                  >
                    {isExpanded
                      ? <ChevronDown className="h-3 w-3 shrink-0 text-text-muted" />
                      : <ChevronRight className="h-3 w-3 shrink-0 text-text-muted" />}
                    <span className="text-[10px] font-mono text-text-muted w-20 shrink-0">{entry.commit_sha.slice(0, 8)}</span>
                    <span className="text-[10px] text-text-muted w-28 shrink-0">{timeAgo(new Date(entry.reviewed_at))}</span>
                    <span className="text-[10px] text-text-muted w-20 shrink-0">{entry.change_type}</span>
                    <span className="text-xs text-text-muted truncate flex-1">{entry.reviewer}</span>
                    <Badge className={`text-[10px] border ${vc.color}`}>
                      <VIcon className="h-2.5 w-2.5 mr-1" />
                      {vc.label}
                    </Badge>
                  </div>
                  {isExpanded && (
                    <div className="ml-6 mt-1 mb-2 p-3 rounded-lg bg-surface-elevated/50 border border-border/50 space-y-2 text-sm">
                      <div className="flex gap-2">
                        <span className="text-text-muted w-28 shrink-0">Commit</span>
                        <span className="text-text-primary font-mono text-xs">{entry.commit_sha}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-text-muted w-28 shrink-0">Reviewer</span>
                        <span className="text-text-primary">{entry.reviewer}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-text-muted w-28 shrink-0">Change type</span>
                        <span className="text-text-primary">{entry.change_type}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-text-muted w-28 shrink-0">Reviewed</span>
                        <span className="text-text-primary">{new Date(entry.reviewed_at).toLocaleString()}</span>
                      </div>
                      {entry.blocking_issues && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-28 shrink-0">Blocking</span>
                          <span className="text-red-400 text-xs">{entry.blocking_issues}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
